// ======================== OFFLINE QUEUE (IndexedDB) ========================
// Stockage local des actions hors ligne avec synchronisation automatique

const DB_NAME = 'aldrins-offline-db';
const DB_VERSION = 1;
const STORE_NAME = 'offline-queue';

// Check if IndexedDB is available
const isIndexedDBAvailable = () => {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch (e) {
    return false;
  }
};

class OfflineQueue {
  constructor() {
    this.db = null;
    this.isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
    this.syncInProgress = false;
    this.listeners = new Set();
    this.isAvailable = isIndexedDBAvailable();
    
    if (typeof window !== 'undefined') {
      this.init();
    }
  }
  
  async init() {
    if (!this.isAvailable) {
      console.warn('[OfflineQueue] IndexedDB not available');
      return;
    }
    
    try {
      this.db = await this.openDatabase();
      
      // Listen for online/offline events
      window.addEventListener('online', () => this.handleOnline());
      window.addEventListener('offline', () => this.handleOffline());
      
      // Listen for SW sync message
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.addEventListener('message', (event) => {
          if (event.data?.type === 'SYNC_OFFLINE_QUEUE') {
            this.syncQueue();
          }
        });
      }
      
      // Initial sync if online
      if (this.isOnline) {
        this.syncQueue();
      }
      
      console.log('[OfflineQueue] Initialized');
    } catch (error) {
      console.error('[OfflineQueue] Init error:', error);
    }
  }
  
  openDatabase() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('status', 'status', { unique: false });
          store.createIndex('type', 'type', { unique: false });
          store.createIndex('created_at', 'created_at', { unique: false });
        }
      };
    });
  }
  
  // ======================== QUEUE OPERATIONS ========================
  
  async addToQueue(action) {
    if (!this.db || !this.isAvailable) {
      console.warn('[OfflineQueue] Cannot add to queue - DB not available');
      return null;
    }
    
    const item = {
      id: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: action.type,
      endpoint: action.endpoint,
      method: action.method || 'POST',
      payload: action.payload,
      status: 'PENDING',
      created_at: new Date().toISOString(),
      attempts: 0,
      last_attempt: null,
      error: null
    };
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      const request = store.add(item);
      request.onsuccess = () => {
        console.log('[OfflineQueue] Added:', item.type);
        this.notifyListeners();
        resolve(item);
      };
      request.onerror = () => reject(request.error);
    });
  }
  
  async getPendingItems() {
    if (!this.db || !this.isAvailable) {
      return [];
    }
    
    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const index = store.index('status');
        
        const request = index.getAll('PENDING');
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
      } catch (e) {
        resolve([]);
      }
    });
  }
  
  async getAllItems() {
    if (!this.db || !this.isAvailable) {
      return [];
    }
    
    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
      } catch (e) {
        resolve([]);
      }
    });
  }
  
  async updateItem(id, updates) {
    if (!this.db || !this.isAvailable) {
      return null;
    }
    
    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        
        const getRequest = store.get(id);
        getRequest.onsuccess = () => {
          const item = getRequest.result;
          if (item) {
            const updated = { ...item, ...updates };
            const putRequest = store.put(updated);
            putRequest.onsuccess = () => {
              this.notifyListeners();
              resolve(updated);
            };
            putRequest.onerror = () => reject(putRequest.error);
          } else {
            reject(new Error('Item not found'));
          }
        };
        getRequest.onerror = () => reject(getRequest.error);
      } catch (e) {
        reject(e);
      }
    });
  }
  
  async deleteItem(id) {
    if (!this.db || !this.isAvailable) {
      return;
    }
    
    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        
        const request = store.delete(id);
        request.onsuccess = () => {
          this.notifyListeners();
          resolve();
        };
        request.onerror = () => reject(request.error);
      } catch (e) {
        reject(e);
      }
    });
  }
  
  async clearSynced() {
    const items = await this.getAllItems();
    const syncedItems = items.filter(i => i.status === 'SYNCED');
    
    for (const item of syncedItems) {
      await this.deleteItem(item.id);
    }
  }
  
  // ======================== SYNC ========================
  
  async syncQueue() {
    if (this.syncInProgress || !this.isOnline) {
      return;
    }
    
    this.syncInProgress = true;
    console.log('[OfflineQueue] Starting sync...');
    
    try {
      const pendingItems = await this.getPendingItems();
      
      // Sort by created_at to maintain order
      pendingItems.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      
      for (const item of pendingItems) {
        await this.syncItem(item);
      }
      
      // Clean up old synced items
      await this.clearSynced();
      
      console.log('[OfflineQueue] Sync complete');
    } catch (error) {
      console.error('[OfflineQueue] Sync error:', error);
    } finally {
      this.syncInProgress = false;
      this.notifyListeners();
    }
  }
  
  async syncItem(item) {
    try {
      // Get auth token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No auth token');
      }
      
      const response = await fetch(item.endpoint, {
        method: item.method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(item.payload)
      });
      
      if (response.ok) {
        await this.updateItem(item.id, {
          status: 'SYNCED',
          last_attempt: new Date().toISOString(),
          response: await response.json()
        });
        console.log('[OfflineQueue] Synced:', item.type);
      } else {
        const errorData = await response.json().catch(() => ({}));
        await this.updateItem(item.id, {
          status: 'FAILED',
          attempts: item.attempts + 1,
          last_attempt: new Date().toISOString(),
          error: errorData.detail || response.statusText
        });
        console.error('[OfflineQueue] Sync failed:', item.type, errorData);
      }
    } catch (error) {
      await this.updateItem(item.id, {
        attempts: item.attempts + 1,
        last_attempt: new Date().toISOString(),
        error: error.message
      });
    }
  }
  
  // ======================== NETWORK EVENTS ========================
  
  handleOnline() {
    console.log('[OfflineQueue] Online');
    this.isOnline = true;
    this.notifyListeners();
    
    // Sync after short delay
    setTimeout(() => this.syncQueue(), 1000);
  }
  
  handleOffline() {
    console.log('[OfflineQueue] Offline');
    this.isOnline = false;
    this.notifyListeners();
  }
  
  // ======================== LISTENERS ========================
  
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }
  
  notifyListeners() {
    this.listeners.forEach((cb) => {
      try {
        cb({
          isOnline: this.isOnline,
          syncInProgress: this.syncInProgress
        });
      } catch (e) {
        console.error('Listener error:', e);
      }
    });
  }
  
  // ======================== HELPERS ========================
  
  getStatus() {
    return {
      isOnline: this.isOnline,
      syncInProgress: this.syncInProgress
    };
  }
}

// Singleton instance
export const offlineQueue = new OfflineQueue();

// Action types that can be queued offline
export const OFFLINE_ACTIONS = {
  CLOCK_IN: 'CLOCK_IN',
  CLOCK_OUT: 'CLOCK_OUT',
  RUNNING_LATE: 'RUNNING_LATE',
  DRAFT_SALE: 'DRAFT_SALE',
  SEND_MESSAGE: 'SEND_MESSAGE'
};

// Actions that REQUIRE online (cannot be queued)
export const ONLINE_ONLY_ACTIONS = [
  'VALIDATE_SALES',
  'CREATE_USER',
  'UPDATE_USER_ROLE',
  'DELETE_USER',
  'CREATE_EXPENSE',
  'UPDATE_EXPENSE',
  'DELETE_EXPENSE',
  'IBAKA_ALERT'
];

export default offlineQueue;
